'use client';

import { BannerClose } from '@heidi/design-system/banner';
import { Button } from '@heidi/design-system/button';
import { Card } from '@heidi/design-system/card';
import {
  TypographyCaptionBold,
  TypographyFootNote,
} from '@heidi/design-system/typography';
import { createLogger } from '@heidi/runtime/logger';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { GET_SESSION_BY_ID, type QuerySessionResponse } from '../../../contracts';
import { noteSuggestionsAtomFamily } from '../../../data/atoms/noteSuggestionsAtoms';
import { useMutateRecordNoteSuggestionFeedback } from '../../../data/mutation/useMutateRecordNoteSuggestionFeedback';
import { applyAllReplacementsOfStrategy } from '../../../data/note-suggestions/applySuggestion';
import { type NoteSuggestionFeedback } from '../../../data/note-suggestions/types';
import { useNoteSuggestionsUiHost } from '../host';
import { type NoteSuggestionPopoverProps } from './note-suggestion-popover.types';

const noteSuggestionLogger = createLogger({
  owner: 'scribe',
  domain: 'sessions',
});

export function NoteSuggestionPopover({
  original,
  replacement,
  mentionCount,
  suggestionId,
  applications,
}: NoteSuggestionPopoverProps) {
  const intl = useIntl();
  const sessionId = useCurrentSessionId() ?? '';
  const setSuggestions = useSetAtom(noteSuggestionsAtomFamily(sessionId));
  const queryClient = useQueryClient();
  const { useSyncNoteToServer } = useNoteSuggestionsUiHost();
  const { mutate: syncNoteToServer } = useSyncNoteToServer();
  const { mutate: recordFeedback } = useMutateRecordNoteSuggestionFeedback();

  const reportFeedback = useCallback(
    (feedback: NoteSuggestionFeedback) => {
      if (!suggestionId) return;
      recordFeedback({ suggestionId, feedback });
    },
    [suggestionId, recordFeedback]
  );

  const onUseAlternative = useCallback(() => {
    const session = queryClient.getQueryData<QuerySessionResponse>([
      GET_SESSION_BY_ID,
      { sessionId },
    ]);
    const currentText = session?.consult_note.result ?? '';
    const nextText = applyAllReplacementsOfStrategy(currentText, applications);
    if (nextText === currentText) {
      noteSuggestionLogger.warn(
        '[note_suggestions] inline use-alternative produced no change',
        { sessionId }
      );
    } else {
      syncNoteToServer({ consultNote: nextText, sessionId });
    }
    reportFeedback('accepted');
    setSuggestions(null);
  }, [
    applications,
    queryClient,
    sessionId,
    syncNoteToServer,
    reportFeedback,
    setSuggestions,
  ]);

  const onKeepAsIs = useCallback(() => {
    reportFeedback('rejected');
    setSuggestions(null);
  }, [reportFeedback, setSuggestions]);

  const onSkip = useCallback(() => {
    reportFeedback('skipped');
    setSuggestions(null);
  }, [reportFeedback, setSuggestions]);

  return (
    // Position + show/hide fade is owned by the body-level Floating UI popup
    // managed in note-suggestion-highlight.ts. This card is just the content.
    <Card className="w-max bg-surface-elevated p-2.5 shadow-lg">
      <div className="mb-2 flex items-center justify-between gap-3">
        <TypographyCaptionBold className="text-text-secondary">
          <FormattedMessage defaultMessage="Which term do you prefer?" id="prYqrIq8ZH" />
        </TypographyCaptionBold>
        <BannerClose
          onClick={onSkip}
          aria-label={intl.formatMessage({
            defaultMessage: 'Dismiss',
            id: 'TDaF6JVgG6',
          })}
        />
      </div>

      <div className="flex flex-row gap-1.5">
        <Button variant="outline" size="sm" onClick={onKeepAsIs}>
          {original}
        </Button>
        <Button variant="outline" size="sm" onClick={onUseAlternative}>
          {replacement}
        </Button>
      </div>

      {mentionCount > 1 && (
        <TypographyFootNote className="mt-2 text-text-tertiary">
          <strong className="text-text-secondary">{mentionCount}</strong>{' '}
          <FormattedMessage defaultMessage="mentions in this note" id="6TtzYfmbu2" />
        </TypographyFootNote>
      )}
    </Card>
  );
}
